import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'

import { ErrorBlockText } from '@/components/widget-components'
import { PaytrieAPI } from '@paytrie/client'
import { useSignupStore } from '@/reducers'
import {
  trackOnboardingStepFailed,
  trackOnboardingStepViewed,
} from '@/utils/analytics'

export const KycRedirect = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [urlLoaded, setUrlLoaded] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [redirectUrl, setRedirectUrl] = useState('')
  const [redirected, setRedirected] = useState<boolean>(false)
  const oliuVerifyType = useSignupStore.use.oliuVerifyType()

  const redirectToKyc = useCallback(async () => {
    try {
      const res = await PaytrieAPI.getUsersMeKycUrl()

      if (res.status === 200) {
        const url = res.data.data.url
        setUrlLoaded(false)
        setRedirected(false)
        setRedirectUrl(url)
        setTimeout(() => window.location.replace(url), 0)
        return
      }

      if (res.status === 400) {
        navigate('/Complete', { replace: true })
        return
      }

      setErrorMessage(t('kyc.error.unableToProcess'))
      trackOnboardingStepFailed(
        'external_kyc_redirect',
        'link_generation_failed'
      )
    } catch {
      setErrorMessage(t('kyc.error.unableToProcess'))
      trackOnboardingStepFailed(
        'external_kyc_redirect',
        'link_generation_failed'
      )
    }
  }, [navigate, t])

  useEffect(() => {
    trackOnboardingStepViewed('external_kyc_redirect')
  }, [])

  useEffect(() => {
    void redirectToKyc()
  }, [redirectToKyc])

  const isIframe = window.self !== window.top

  return (
    <div className="flex flex-col gap-5" data-testid="container-kyc-redirect">
      {!urlLoaded && !redirected && (
        <div
          className="flex justify-between"
          data-testid="text-kyc-redirect-redirecting"
        >
          {t('kyc.redirecting')}
        </div>
      )}

      {redirected && (
        <div
          className="flex justify-between"
          data-testid="text-kyc-redirect-complete"
        >
          {t('kyc.redirectComplete')}
        </div>
      )}

      {errorMessage && (
        <ErrorBlockText data-testid="error-kyc-redirect-message">
          {errorMessage}
        </ErrorBlockText>
      )}

      {redirectUrl.length > 4 && (
        <div data-testid="container-kyc-redirect-verification">
          <div
            className="text-xl font-normal uppercase"
            style={{ paddingBottom: '1em' }}
            data-testid="text-kyc-redirect-title"
          >
            {oliuVerifyType === 'bank' ? 'BANK' : 'ID'} VERIFICATION
          </div>

          <div
            className={`flex flex-col ${isIframe ? 'items-center' : 'items-start'} gap-6`}
            data-testid="container-kyc-redirect-qr"
          >
            <div
              style={{ paddingTop: '3em' }}
              data-testid="text-kyc-redirect-instruction"
            >
              {t('kyc.qrInstruction')}
            </div>
            <div className="flex" data-testid="container-kyc-redirect-qrcode">
              <QRCode value={redirectUrl} />
            </div>
          </div>

          <div
            style={{
              textAlign: 'left',
              paddingTop: '50px',
              paddingBottom: '20px',
            }}
            data-testid="container-kyc-redirect-desktop-link"
          >
            <a
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => {
                window.location.href = redirectUrl
              }}
              data-testid="link-kyc-redirect-desktop"
            >
              {t('kyc.desktopLink')}
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
